// Feature: MCRO001-F0023
// CI test: T-MCRO-023
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { choreographyApi } from '../../services/api';
import './MusicChoreography.css';

const RETURN_HANDLE_PREFIX = 'studioaeye.review.return-handle';
const unavailableMessage = 'This review invitation is not available. Ask the sender for a new invitation.';
const payload = (response) => response?.data?.data || response?.data || {};

const createContextId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const reviewHandleKey = (contextId) => `${RETURN_HANDLE_PREFIX}:${contextId}`;

export function bootstrapInvitationContext(location = globalThis.location) {
  const fragment = String(location?.hash || '').replace(/^#/, '');
  const params = new URLSearchParams(fragment);
  let bareToken = '';
  try { bareToken = fragment.includes('=') ? '' : decodeURIComponent(fragment); } catch { bareToken = ''; }
  const token = params.get('invite') || params.get('token') || bareToken;
  const query = new URLSearchParams(location?.search || '');
  let contextId = query.get('reviewContext') || '';
  if (token && !contextId) { contextId = createContextId(); query.set('reviewContext', contextId); }
  const search = query.toString();
  return {
    token: token || '',
    contextId,
    hadFragment: Boolean(fragment),
    returnPath: `${location?.pathname || '/choreography/review'}${search ? `?${search}` : ''}`,
  };
}

function safeReviewPath(value) {
  if (typeof value !== 'string' || !value.startsWith('/')) return '';
  if (value.startsWith('//') || !/^\/app\/choreography(?:\/|\?|$)/.test(value)) return '';
  return value;
}

export default function ReviewInvitation() {
  const bootstrapRef = React.useRef(null);
  if (!bootstrapRef.current) bootstrapRef.current = bootstrapInvitationContext();
  const contextId = bootstrapRef.current.contextId;
  const storageKey = contextId ? reviewHandleKey(contextId) : '';
  const returnPath = bootstrapRef.current.returnPath;
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [handle, setHandle] = useState(() => storageKey ? (globalThis.sessionStorage?.getItem(storageKey) || '') : '');
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('Checking this review invitation…');
  const [creating, setCreating] = useState(false);
  const [registration, setRegistration] = useState({ displayName: '', email: '', password: '' });

  useEffect(() => {
    let active = true;
    const bootstrap = bootstrapRef.current;
    // Scrub before the first network call. Keeping this side effect out of
    // render prevents React StrictMode's discarded render from consuming the
    // only in-memory copy of the fragment token.
    if (bootstrap.hadFragment) globalThis.history?.replaceState?.(null, '', returnPath);
    if (!bootstrap.inspection && bootstrap.token) {
      bootstrap.inspection = choreographyApi.inspectReviewInvitation(bootstrap.token);
      bootstrap.token = '';
    }
    if (!bootstrap.inspection) {
      const savedHandle = storageKey ? (globalThis.sessionStorage?.getItem(storageKey) || '') : '';
      if (savedHandle) {
        setHandle(savedHandle);
        setState('ready');
        setMessage('Sign in to continue this review invitation.');
      } else {
        setState('unavailable');
        setMessage(unavailableMessage);
      }
      return () => { active = false; };
    }
    bootstrap.inspection.then((response) => {
      if (!active) return;
      const inspected = payload(response);
      if (!inspected.handle || inspected.available === false || !storageKey) throw new Error('unavailable');
      globalThis.sessionStorage?.setItem(storageKey, inspected.handle);
      setHandle(inspected.handle);
      setLanding({
        resourceLabel: inspected.resource?.label || inspected.resourceLabel || inspected.invitationLabel || 'a choreography Sheet',
        studioName: inspected.studioName || '',
      });
      setState('ready');
      setMessage('');
    }).catch(() => {
      if (!active) return;
      if (storageKey) globalThis.sessionStorage?.removeItem(storageKey);
      setHandle('');
      setLanding(null);
      setState('unavailable');
      setMessage(unavailableMessage);
    });
    return () => { active = false; };
  }, []); // Inspect the fragment once; the raw token is removed immediately.

  const register = async (event) => {
    event.preventDefault();
    if (!handle || !registration.displayName.trim() || !registration.email.trim() || !registration.password) return;
    setState('registering');
    setMessage('Starting secure email verification…');
    try {
      await choreographyApi.registerReviewAccount({
        handle,
        displayName: registration.displayName.trim(),
        email: registration.email.trim(),
        password: registration.password,
      });
      setRegistration((value) => ({ ...value, password: '' }));
      setCreating(false);
      setState('verification');
      setMessage('Check your email to verify this review account. Then return here and sign in; no studio membership will be created.');
    } catch {
      setState('ready');
      setMessage('We could not start account creation. Check the invitation details or ask the sender for a new invitation.');
    }
  };

  const redeem = async () => {
    if (!handle || !isAuthenticated) return;
    setState('redeeming');
    setMessage('Connecting this Sheet to your account…');
    try {
      const redeemed = payload(await choreographyApi.redeemReviewInvitation(handle));
      if (storageKey) globalThis.sessionStorage?.removeItem(storageKey);
      setHandle('');
      const returnPath = safeReviewPath(redeemed.returnPath || redeemed.reviewPath);
      if (returnPath) {
        navigate(returnPath, { replace: true });
        return;
      }
      setState('complete');
      setMessage('Review access is ready. You can return with this account without using the invitation again.');
    } catch {
      setState('unavailable');
      setMessage('We could not connect this invitation to this account. Check that you signed in with the invited email, or ask the sender for a new invitation.');
    }
  };

  const validInvitation = state === 'ready' || state === 'redeeming' || state === 'registering';
  return <main className="mc-shared-page"><section className="mc-panel mc-shared-card mc-review-invitation" aria-labelledby="review-invitation-title">
    <span className="mc-kicker">Choreo Sheet review</span>
    <h1 id="review-invitation-title">{landing?.resourceLabel || 'Review invitation'}</h1>
    {landing?.studioName && <p className="mc-muted">Invited by {landing.studioName}</p>}
    <p role="status" aria-live="polite">{message}</p>
    {validInvitation && <>
      <p>Sign in to add comments, pictures or video. You do not need to join the studio.</p>
      {isAuthenticated
        ? <button type="button" className="mc-primary" onClick={redeem} disabled={state === 'redeeming'}>{state === 'redeeming' ? 'Connecting…' : 'Review this Sheet'}</button>
        : <><div className="mc-review-invitation-actions"><Link className="mc-primary" to="/login" state={{ from: { pathname: returnPath } }}>Sign in</Link><button type="button" className="mc-quiet" onClick={() => setCreating(true)}>Create account to review</button></div>
          {creating && <form className="mc-review-registration" aria-label="Create review account" onSubmit={register}>
            <p>This account is bound to this invitation and creates no studio membership.</p>
            <label>Display name<input aria-label="Reviewer display name" value={registration.displayName} onChange={(event) => setRegistration((value) => ({ ...value, displayName: event.target.value }))} required /></label>
            <label>Invited email<input aria-label="Invited email" type="email" value={registration.email} onChange={(event) => setRegistration((value) => ({ ...value, email: event.target.value }))} required /></label>
            <label>Password<input aria-label="Review account password" type="password" autoComplete="new-password" value={registration.password} onChange={(event) => setRegistration((value) => ({ ...value, password: event.target.value }))} required /></label>
            <div><button type="button" onClick={() => setCreating(false)}>Cancel</button><button type="submit" className="mc-primary" disabled={state === 'registering'}>{state === 'registering' ? 'Starting verification…' : 'Verify email to create account'}</button></div>
          </form>}</>}
    </>}
    {state === 'verification' && <Link className="mc-primary" to="/login" state={{ from: { pathname: returnPath } }}>Sign in after verification</Link>}
    {state === 'complete' && <Link className="mc-primary" to="/app/choreography">Open Choreo</Link>}
  </section></main>;
}
